import React from 'react'
import { PersonConnection } from '../../../types'
import EntityProfileLink from '../../watchlist/EntityProfileLink'

/**
 * Ranked list of business connections — other persons who share active
 * company affiliations with the subject.  Sorted by `sharedCompanyCount`
 * then `totalOverlapDays`.
 */
interface PersonConnectionsListProps {
  connections: PersonConnection[]
  loading: boolean
  error: string | null
}

function formatOverlapYears(days: number): string {
  if (days <= 0) return ''
  const years = Math.floor(days / 365)
  const months = Math.floor((days % 365) / 30)
  const parts: string[] = []
  if (years > 0) parts.push(`${years} år`)
  if (months > 0) parts.push(`${months} mdr.`)
  if (parts.length === 0) parts.push('< 1 mdr.')
  return parts.join(' ')
}

const PersonConnectionsList: React.FC<PersonConnectionsListProps> = ({
  connections,
  loading,
  error,
}) => {
  if (loading) {
    return (
      <p className="font-mono text-xs uppercase tracking-widest text-ink-500 py-4">
        Beregner forbindelser…
      </p>
    )
  }

  if (error) {
    return (
      <p className="text-sm text-signal-900 bg-signal-50 border border-signal-200 rounded-sm px-3 py-2">
        {error}
      </p>
    )
  }

  if (connections.length === 0) {
    return (
      <p className="text-sm text-ink-500">
        Ingen forbindelser fundet. Det kan skyldes, at personen ikke har aktive selskaber,
        eller at selskaberne ikke har andre registrerede personer som meddeltagere.
      </p>
    )
  }

  return (
    <ul className="list-none divide-y divide-rule border-t border-rule">
      {connections.map((conn) => {
        const overlapLabel = formatOverlapYears(conn.totalOverlapDays)
        return (
          <li key={conn.enhedsNummer} className="py-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <EntityProfileLink
                  to={`/person/${conn.enhedsNummer}`}
                  entityType="person"
                  entityId={String(conn.enhedsNummer)}
                  className="font-medium text-ink hover:underline decoration-signal underline-offset-4"
                >
                  {conn.navn || <em className="text-ink-400">Uden navn</em>}
                </EntityProfileLink>
                <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  {conn.sharedCompanies.map((sc) => (
                    <li key={sc.cvrNummer}>
                      <EntityProfileLink
                        to={`/company/${sc.cvrNummer}`}
                        entityType="company"
                        entityId={String(sc.cvrNummer)}
                        className="font-mono text-xs text-ink-500 hover:text-ink hover:underline decoration-rule-strong underline-offset-4"
                      >
                        {sc.navn || sc.cvrNummer}
                      </EntityProfileLink>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0 text-right">
                <span className="font-mono text-sm text-ink tabular-nums">
                  {conn.sharedCompanyCount}{' '}
                  {conn.sharedCompanyCount === 1 ? 'fælles selskab' : 'fælles selskaber'}
                </span>
                {overlapLabel && (
                  <span className="text-xs text-ink-500" title="Samlet overlappende periode">
                    {overlapLabel} overlap
                  </span>
                )}
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export default PersonConnectionsList
